import { NotFoundError } from '@cian/peperrors/shared';
import { Matcher } from '../matcher.mjs';
import { SortedWritableSource } from '../sources/sorted/SortedWritableSource.mjs';
import { ContextualTransformer } from '../transformers/ContextualTransformer.mjs';

class Config {
    sources;
    matcher;
    transformers = [];
    constructor({ source, matcher, transformer }) {
        this.sources = Array.isArray(source) ? source : [source];
        this.matcher = matcher || new Matcher();
        if (transformer) {
            this.transformers = Array.isArray(transformer) ? transformer : [transformer];
        }
    }
    attach(context) {
        return new ContextualConfig({
            context: { ...context },
            source: this.sources.slice(),
            matcher: this.matcher,
            transformer: this.transformers.slice(),
        });
    }
    get(key, context) {
        const setting = this.find(key, context);
        return setting && setting.value;
    }
    getStrict(key, context) {
        const setting = this.find(key, context);
        if (setting === null) {
            throw new NotFoundError({
                message: `Setting '${key}' is missing in the config!`,
                domain: 'config.getStrict',
            });
        }
        return setting.value;
    }
    has(key, context) {
        return this.find(key, context) !== null;
    }
    dump(keys) {
        if (!keys) {
            return this.sources.reduce((result, source) => {
                result.push(...source.selectAll());
                return result;
            }, []);
        }
        const dumpKeys = Array.isArray(keys) ? keys : [keys];
        return dumpKeys.reduce((result, key) => {
            for (const source of this.sources) {
                result.push(...source.selectAll(key));
            }
            return result;
        }, []);
    }
    dumpForContext(keys, context) {
        const dumpKeys = Array.isArray(keys) ? keys : [keys];
        return dumpKeys.reduce((result, key) => {
            const setting = this.find(key, context);
            if (setting) {
                result.push(setting);
            }
            return result;
        }, []);
    }
    find(key, context) {
        let findedSetting = null;
        const settingFilter = (setting) => {
            return this.matcher.matches(setting, context);
        };
        for (const source of this.sources) {
            const setting = source.select(key, settingFilter);
            if (!setting) {
                continue;
            }
            if (!findedSetting || findedSetting.priority < setting.priority) {
                findedSetting = setting;
            }
        }
        return this.settingTransform(findedSetting);
    }
    settingTransform(setting) {
        if (!this.transformers.length || !setting) {
            return setting;
        }
        return this.transformers.reduce((result, transformer) => {
            return transformer.transform(result);
        }, setting);
    }
}
class ContextualConfig extends Config {
    context;
    contextualSource;
    contextualTransformer;
    constructor(props) {
        super(props);
        const { context } = props;
        this.context = props.context;
        this.contextualSource = new SortedWritableSource();
        this.sources.push(this.contextualSource);
        this.contextualTransformer = new ContextualTransformer(context);
    }
    attach(context) {
        return super.attach({ ...this.context, ...context });
    }
    get(key) {
        return super.get(key, this.context);
    }
    getStrict(key) {
        return super.getStrict(key, this.context);
    }
    has(key) {
        return super.has(key, this.context);
    }
    dumpForContext(keys) {
        return super.dumpForContext(keys, this.context);
    }
    injectSettings(settings) {
        return this.contextualSource.add(settings);
    }
    settingTransform(setting) {
        const superTransformed = super.settingTransform(setting);
        return superTransformed && this.contextualTransformer.transform(superTransformed);
    }
}

export { Config, ContextualConfig };
//# sourceMappingURL=config.mjs.map
